import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import axios from "axios";
import { TwoFactorView, PasswordResetView } from "@/components/auth/AuthViews";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESEND_COOLDOWN = 30;

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [show2FA, setShow2FA] = useState(false);
  const [sessionToken, setSessionToken] = useState("");
  const [otp, setOtp] = useState("");
  const [otpMessage, setOtpMessage] = useState("");
  const [emailFailed, setEmailFailed] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetEmail, setResetEmail] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const { login, user } = useAuth();

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Please enter both email and password.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      if (response.data.requires_2fa) {
        setSessionToken(response.data.session_token);
        setOtpMessage(response.data.message);
        setShow2FA(true);
        setResendCooldown(RESEND_COOLDOWN);
        if (response.data.email_sent === false) {
          setEmailFailed(true);
          toast.warning("Email delivery failed. Please try 'Resend OTP' below.", { duration: 8000 });
        } else {
          setEmailFailed(false);
          toast.info(response.data.message);
        }
      } else {
        login(response.data.token, response.data.user);
        toast.success(`Welcome back, ${response.data.user?.name || "User"}!`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Invalid email or password. Please check your input.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/verify-otp`, { session_token: sessionToken, otp });
      login(response.data.token, response.data.user);
      toast.success(`Welcome back, ${response.data.user?.name || "User"}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = useCallback(async () => {
    if (resendCooldown > 0) return;
    setResending(true);
    try {
      const response = await axios.post(`${API_URL}/auth/resend-otp`, { session_token: sessionToken });
      if (response.data.email_sent === false) {
        setEmailFailed(true);
        toast.warning("Email delivery failed. Please try again.", { duration: 6000 });
      } else {
        setEmailFailed(false);
        toast.success(response.data.message);
      }
      setResendCooldown(RESEND_COOLDOWN);
      setOtp("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend OTP");
    } finally {
      setResending(false);
    }
  }, [resendCooldown, sessionToken]);

  const handleForgotPasswordOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email: resetEmail });
      if (response.data.email_sent === false) {
        toast.warning("Email delivery failed. Please try again.", { duration: 6000 });
      } else {
        toast.success(response.data.message);
      }
      setResetStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    if (newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, { email: resetEmail, otp: resetOtp, new_password: newPassword });
      toast.success("Password reset successfully! Please log in.");
      setShowPasswordReset(false);
      setResetStep(1);
      setResetEmail("");
      setResetOtp("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => { setShow2FA(false); setOtp(""); setSessionToken(""); setEmailFailed(false); };
  const bgImage = `url('https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1920')`;

  if (showPasswordReset) {
    return (
      <PasswordResetView
        resetStep={resetStep} resetEmail={resetEmail} setResetEmail={setResetEmail}
        resetOtp={resetOtp} setResetOtp={setResetOtp} newPassword={newPassword} setNewPassword={setNewPassword}
        confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
        loading={loading} onRequestOTP={handleForgotPasswordOTP} onResetPassword={handleResetPassword}
        onBack={() => { setShowPasswordReset(false); setResetStep(1); }} bgImage={bgImage}
      />
    );
  }

  if (show2FA) {
    return (
      <TwoFactorView
        otp={otp} setOtp={setOtp} otpMessage={otpMessage} emailFailed={emailFailed}
        loading={loading} resendCooldown={resendCooldown} resending={resending}
        onVerify={handleVerifyOTP} onResend={handleResendOTP} onBack={backToLogin} bgImage={bgImage}
      />
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="flex-1 flex items-center justify-center p-4 md:p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-black text-slate-900 uppercase mb-2" style={{ fontFamily: "Chivo, sans-serif", letterSpacing: "-0.02em" }}>DOZERWORLD</h1>
            <p className="text-slate-500">Inventory Management System</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <KeyRound className="text-orange-500" size={28} />
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 uppercase" style={{ fontFamily: "Chivo, sans-serif" }}>Sign In</h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-xs text-slate-600 font-bold uppercase">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" className="mt-1 rounded-sm border-slate-300" autoFocus data-testid="email-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 font-bold uppercase">Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Enter your password" className="mt-1 rounded-sm border-slate-300" data-testid="password-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-orange-600 text-white hover:bg-orange-700 rounded-sm uppercase font-bold tracking-wide" data-testid="login-button">
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
            <button onClick={() => setShowPasswordReset(true)} className="mt-4 text-sm text-orange-600 hover:text-orange-700 font-medium" data-testid="forgot-password-btn">
              Forgot Password?
            </button>
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">DozerWorld Inventory System v2.0</p>
        </div>
      </div>
      <div className="hidden lg:block flex-1 bg-cover bg-center relative" style={{ backgroundImage: bgImage }}>
        <div className="absolute inset-0 bg-slate-900/40"></div>
      </div>
    </div>
  );
};

export default Login;
